/**
 * Loads every publisher account config the user can see for a publisher — optionally narrowed to
 * the caller's business units — and reduces them to the countries a campaign can target, so
 * country pickers reflect configured accounts rather than a hardcoded per-publisher list.
 */
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useMemo } from 'react';
import { Countries } from 'src/store/constants';
import type { CampaignDesiredStateDto, Country } from 'src/store/generated/leela-api-generated';
import { useGetAllPublisherAccountConfigsQuery } from 'src/store/leela-api';
import { deriveCountriesFromPublisherConfigs } from 'src/utils/publisherConfigCountries';

interface PublisherConfigCountriesResult {
  readonly countries: Country[];
  readonly isLoading: boolean;
}

export const usePublisherConfigCountries = (
  publisher: CampaignDesiredStateDto['publisher'] | undefined,
  businessUnitIds: readonly string[] = [],
): PublisherConfigCountriesResult => {
  const { data, isFetching } = useGetAllPublisherAccountConfigsQuery(
    publisher ? { publisher, ...(businessUnitIds.length > 0 && { businessUnitIds }) } : skipToken,
  );

  // Hide the previous publisher's countries while a refetch for the new publisher is in flight.
  const isLoading = Boolean(publisher) && Boolean(isFetching);
  const countries = useMemo(
    () => (isLoading ? [] : deriveCountriesFromPublisherConfigs(data, Countries)),
    [data, isLoading],
  );

  return { countries, isLoading };
};
